<template>
  <div class="moqu-direct-limit-config">
    <el-card>
      <div slot="header" class="clearfix">
        <span>魔趣直连额度配置</span>
      </div>

      <div class="current-status" v-loading="loading">
        <div class="status-item">
          <div class="label">当前全能卡每日直连额度</div>
          <div class="value">{{ config.premiumDailyLimit }} 次</div>
        </div>
        <div class="status-item">
          <div class="label">专属覆盖用户数</div>
          <div class="value small">{{ config.overrideCount || 0 }} 人</div>
        </div>
        <div class="status-item" v-if="config.updatedAt">
          <div class="label">最近更新时间</div>
          <div class="value small">{{ formatTime(config.updatedAt) }}</div>
        </div>
      </div>

      <el-divider></el-divider>

      <el-form :model="form" :rules="rules" ref="limitForm" label-width="220px" style="max-width: 680px;">
        <el-form-item label="设置每日直连额度" prop="premium_daily_limit">
          <el-input-number
            v-model="form.premium_daily_limit"
            :min="1"
            :max="999"
            :step="1"
            controls-position="right"
          />
          <div class="tip">默认20，支持改成10/30等，立即生效；用户专属额度优先生效</div>
        </el-form-item>

        <el-form-item>
          <el-button type="primary" :loading="saving" @click="saveConfig">保存配置</el-button>
          <el-button @click="loadConfig">刷新</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <el-card class="override-card">
      <div slot="header" class="override-header">
        <span>用户专属额度覆盖</span>
        <div class="actions">
          <el-input
            v-model="overrideQuery.keyword"
            placeholder="输入用户ID或用户名"
            clearable
            size="small"
            style="width: 220px;"
            @keyup.enter.native="handleSearch"
            @clear="handleSearch"
          />
          <el-button size="small" icon="el-icon-search" @click="handleSearch">查询</el-button>
          <el-button size="small" @click="handleReset">重置</el-button>
          <el-button type="primary" size="small" @click="openCreateDialog">新增覆盖</el-button>
        </div>
      </div>

      <el-table
        v-loading="overrideLoading"
        :data="overrideTableData"
        border
        style="width: 100%"
        empty-text="暂无专属额度覆盖"
      >
        <el-table-column prop="userId" label="用户ID" width="100" />
        <el-table-column prop="userName" label="用户名" min-width="160">
          <template slot-scope="scope">
            {{ scope.row.userName || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="vipType" label="VIP类型" width="120">
          <template slot-scope="scope">
            <el-tag size="mini" type="success" v-if="scope.row.vipType === 'premium'">全能卡</el-tag>
            <el-tag size="mini" v-else>{{ formatVipType(scope.row.vipType) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="premiumDailyLimit" label="专属每日额度" width="140" />
        <el-table-column prop="updatedAt" label="更新时间" min-width="180">
          <template slot-scope="scope">
            {{ formatTime(scope.row.updatedAt) }}
          </template>
        </el-table-column>
        <el-table-column label="更新人" min-width="140">
          <template slot-scope="scope">
            {{ scope.row.updatedByName || scope.row.updatedBy || '-' }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="160" fixed="right">
          <template slot-scope="scope">
            <el-button type="text" @click="openEditDialog(scope.row)">编辑</el-button>
            <el-button type="text" class="delete-button" @click="handleDeleteOverride(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-container">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="overridePagination.page"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="overridePagination.pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="overridePagination.total"
        />
      </div>
    </el-card>

    <el-dialog
      :title="dialogMode === 'edit' ? '编辑用户专属额度' : '新增用户专属额度'"
      :visible.sync="dialogVisible"
      width="500px"
      :close-on-click-modal="false"
      @closed="resetDialogForm"
    >
      <el-form
        ref="overrideFormRef"
        :model="overrideForm"
        :rules="overrideRules"
        label-width="120px"
      >
        <el-form-item label="用户名" prop="userName">
          <el-input
            v-model.trim="overrideForm.userName"
            placeholder="请输入用户名"
            :disabled="dialogMode === 'edit'"
            clearable
          />
          <div class="tip" v-if="dialogMode === 'edit'">编辑场景用户名不可修改；如需更换用户请删除后新增</div>
        </el-form-item>
        <el-form-item label="每日额度" prop="premium_daily_limit">
          <el-input-number
            v-model="overrideForm.premium_daily_limit"
            :min="1"
            :max="999"
            :precision="0"
            :step="1"
            controls-position="right"
            style="width: 100%"
          />
          <div class="tip">仅支持全能卡用户；保存后立即覆盖全局额度</div>
        </el-form-item>
      </el-form>

      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取 消</el-button>
        <el-button type="primary" :loading="overrideSubmitting" @click="submitOverride">确 定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  getMoquDirectLimitConfig,
  updateMoquDirectLimitConfig,
  getMoquDirectUserOverrides,
  getMoquDirectUserOverride,
  upsertMoquDirectUserOverride,
  upsertMoquDirectUserOverrideByName,
  deleteMoquDirectUserOverride
} from '@/api'

export default {
  name: 'MoquDirectLimitConfig',
  data() {
    return {
      loading: false,
      saving: false,
      config: {
        premiumDailyLimit: 20,
        updatedAt: null,
        updatedBy: null,
        defaultLimit: 20,
        overrideCount: 0
      },
      form: {
        premium_daily_limit: 20
      },
      rules: {
        premium_daily_limit: [
          {
            validator: (rule, value, callback) => {
              const parsed = parseInt(value, 10)
              if (!Number.isInteger(parsed) || parsed <= 0) {
                callback(new Error('请输入大于0的整数'))
                return
              }
              callback()
            },
            trigger: 'blur'
          }
        ]
      },

      overrideLoading: false,
      overrideTableData: [],
      overrideQuery: {
        keyword: ''
      },
      overridePagination: {
        page: 1,
        pageSize: 20,
        total: 0
      },

      dialogVisible: false,
      dialogMode: 'create',
      overrideSubmitting: false,
      overrideForm: {
        userId: null,
        userName: '',
        premium_daily_limit: 20
      },
      overrideRules: {
        userName: [
          {
            validator: (rule, value, callback) => {
              const parsed = String(value || '').trim()
              if (!parsed) {
                callback(new Error('请输入用户名'))
                return
              }
              callback()
            },
            trigger: 'blur'
          }
        ],
        premium_daily_limit: [
          {
            validator: (rule, value, callback) => {
              const parsed = parseInt(value, 10)
              if (!Number.isInteger(parsed) || parsed <= 0) {
                callback(new Error('请输入大于0的整数'))
                return
              }
              callback()
            },
            trigger: 'blur'
          }
        ]
      }
    }
  },
  mounted() {
    this.loadConfig()
    this.loadUserOverrides()
  },
  methods: {
    async loadConfig() {
      this.loading = true
      try {
        const res = await getMoquDirectLimitConfig()
        if (res.success && res.data) {
          this.config = {
            premiumDailyLimit: res.data.premiumDailyLimit || 20,
            updatedAt: res.data.updatedAt || null,
            updatedBy: res.data.updatedBy || null,
            defaultLimit: res.data.defaultLimit || 20,
            overrideCount: res.data.overrideCount || 0
          }
          this.form.premium_daily_limit = this.config.premiumDailyLimit
        } else {
          this.$message.error(res.message || '获取配置失败')
        }
      } catch (err) {
        console.error('获取魔趣直连额度配置失败:', err)
        this.$message.error(err.response?.data?.message || '获取配置失败')
      } finally {
        this.loading = false
      }
    },

    saveConfig() {
      this.$refs.limitForm.validate(async (valid) => {
        if (!valid) return

        this.saving = true
        try {
          const payload = {
            premium_daily_limit: parseInt(this.form.premium_daily_limit, 10)
          }
          const res = await updateMoquDirectLimitConfig(payload)
          if (res.success) {
            this.$message.success('保存成功')
            await this.loadConfig()
          } else {
            this.$message.error(res.message || '保存失败')
          }
        } catch (err) {
          console.error('保存魔趣直连额度配置失败:', err)
          this.$message.error(err.response?.data?.message || '保存失败')
        } finally {
          this.saving = false
        }
      })
    },

    async loadUserOverrides() {
      this.overrideLoading = true
      try {
        const params = {
          page: this.overridePagination.page,
          pageSize: this.overridePagination.pageSize,
          keyword: this.overrideQuery.keyword ? this.overrideQuery.keyword.trim() : ''
        }

        const res = await getMoquDirectUserOverrides(params)
        if (res.success && res.data) {
          this.overrideTableData = Array.isArray(res.data.list) ? res.data.list : []
          this.overridePagination.total = res.data.pagination?.total || 0
          this.overridePagination.page = res.data.pagination?.page || this.overridePagination.page
          this.overridePagination.pageSize = res.data.pagination?.pageSize || this.overridePagination.pageSize
        } else {
          this.$message.error(res.message || '获取用户专属额度列表失败')
        }
      } catch (err) {
        console.error('获取用户专属额度列表失败:', err)
        this.$message.error(err.response?.data?.message || '获取用户专属额度列表失败')
      } finally {
        this.overrideLoading = false
      }
    },

    handleSearch() {
      this.overridePagination.page = 1
      this.loadUserOverrides()
    },

    handleReset() {
      this.overrideQuery.keyword = ''
      this.overridePagination.page = 1
      this.loadUserOverrides()
    },

    handleSizeChange(pageSize) {
      this.overridePagination.pageSize = pageSize
      this.overridePagination.page = 1
      this.loadUserOverrides()
    },

    handleCurrentChange(page) {
      this.overridePagination.page = page
      this.loadUserOverrides()
    },

    openCreateDialog() {
      this.dialogMode = 'create'
      this.overrideForm = {
        userId: null,
        userName: '',
        premium_daily_limit: this.config.premiumDailyLimit || 20
      }
      this.dialogVisible = true
      this.$nextTick(() => {
        this.$refs.overrideFormRef && this.$refs.overrideFormRef.clearValidate()
      })
    },

    async openEditDialog(row) {
      this.dialogMode = 'edit'
      try {
        const res = await getMoquDirectUserOverride(row.userId)
        if (res.success && res.data?.hasOverride && res.data.override) {
          this.overrideForm = {
            userId: res.data.override.userId,
            userName: res.data.override.userName || row.userName || '',
            premium_daily_limit: res.data.override.premiumDailyLimit
          }
        } else {
          this.overrideForm = {
            userId: row.userId,
            userName: row.userName || '',
            premium_daily_limit: row.premiumDailyLimit
          }
        }
      } catch (err) {
        console.error('获取用户专属额度详情失败:', err)
        this.overrideForm = {
          userId: row.userId,
          userName: row.userName || '',
          premium_daily_limit: row.premiumDailyLimit
        }
      }

      this.dialogVisible = true
      this.$nextTick(() => {
        this.$refs.overrideFormRef && this.$refs.overrideFormRef.clearValidate()
      })
    },

    submitOverride() {
      this.$refs.overrideFormRef.validate(async (valid) => {
        if (!valid) return

        this.overrideSubmitting = true
        try {
          const payload = {
            premium_daily_limit: parseInt(this.overrideForm.premium_daily_limit, 10)
          }

          let res = null
          if (this.dialogMode === 'create') {
            const userName = String(this.overrideForm.userName || '').trim()
            res = await upsertMoquDirectUserOverrideByName({
              user_name: userName,
              ...payload
            })
          } else {
            const userId = parseInt(this.overrideForm.userId, 10)
            res = await upsertMoquDirectUserOverride(userId, payload)
          }

          if (res.success) {
            this.$message.success('保存用户专属额度成功')
            this.dialogVisible = false
            await this.loadConfig()
            await this.loadUserOverrides()
          } else {
            this.$message.error(res.message || '保存用户专属额度失败')
          }
        } catch (err) {
          console.error('保存用户专属额度失败:', err)
          this.$message.error(err.response?.data?.message || '保存用户专属额度失败')
        } finally {
          this.overrideSubmitting = false
        }
      })
    },

    handleDeleteOverride(row) {
      this.$confirm(`确认删除用户 ${row.userId} 的专属额度吗？删除后将恢复全局额度。`, '提示', {
        type: 'warning'
      })
        .then(async () => {
          try {
            const res = await deleteMoquDirectUserOverride(row.userId)
            if (res.success) {
              this.$message.success(res.message || '删除成功')
              if (this.overrideTableData.length === 1 && this.overridePagination.page > 1) {
                this.overridePagination.page -= 1
              }
              await this.loadConfig()
              await this.loadUserOverrides()
            } else {
              this.$message.error(res.message || '删除失败')
            }
          } catch (err) {
            console.error('删除用户专属额度失败:', err)
            this.$message.error(err.response?.data?.message || '删除用户专属额度失败')
          }
        })
        .catch(() => {})
    },

    resetDialogForm() {
      this.overrideSubmitting = false
      this.overrideForm = {
        userId: null,
        userName: '',
        premium_daily_limit: this.config.premiumDailyLimit || 20
      }
      this.$refs.overrideFormRef && this.$refs.overrideFormRef.clearValidate()
    },

    formatVipType(vipType) {
      if (vipType === 'premium') return '全能卡'
      if (vipType === 'monthly') return '月卡'
      if (vipType === 'yearly') return '年卡'
      if (!vipType) return '-'
      return vipType
    },

    formatTime(time) {
      if (!time) return '-'
      const date = new Date(time)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hour = String(date.getHours()).padStart(2, '0')
      const minute = String(date.getMinutes()).padStart(2, '0')
      const second = String(date.getSeconds()).padStart(2, '0')
      return `${year}-${month}-${day} ${hour}:${minute}:${second}`
    }
  }
}
</script>

<style scoped>
.moqu-direct-limit-config {
  padding: 0;
}

.current-status {
  display: flex;
  gap: 32px;
  flex-wrap: wrap;
}

.status-item .label {
  color: #909399;
  font-size: 13px;
  margin-bottom: 6px;
}

.status-item .value {
  color: #303133;
  font-size: 28px;
  font-weight: 600;
}

.status-item .value.small {
  font-size: 16px;
  font-weight: 500;
}

.tip {
  margin-top: 8px;
  color: #909399;
  font-size: 12px;
}

.override-card {
  margin-top: 16px;
}

.override-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pagination-container {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
}

.delete-button {
  color: #f56c6c;
}
</style>
